import {
    FormControl,
    FormHelperText,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    type FormHelperTextProps,
    type SelectProps,
} from '@mui/material';
import type { ReactNode, RefObject } from 'react';
import { tss } from 'tss-react/mui';

const useStyles = tss
    .withNestedSelectors<'secondary'>()
    .create(({ theme, classes }) => ({
        helperText: {
            color: theme.bino.labelColor,
        },
        errorText: {
            color: theme.bino.color.red,
        },
        hasSecondary: {
            height: 'auto',
        },
        primary: {},
        secondary: {
            whiteSpace: 'normal',
        },
        select: {
            [`& .${classes.secondary}`]: {
                display: 'none',
            },
        },
    }));

export type SelectFieldProps<Value = unknown> = Pick<
    SelectProps<Value | string>,
    | 'label'
    | 'name'
    | 'value'
    | 'readOnly'
    | 'onChange'
    | 'fullWidth'
    | 'className'
    | 'required'
    | 'margin'
> & {
    FormHelperTextProps?: FormHelperTextProps;
    helperText?: ReactNode;
    errorText?: ReactNode;
    rootRef?: RefObject<HTMLInputElement>;
    options: ReadonlyArray<{
        value: string;
        label: ReactNode;
        description?: ReactNode;
    }>;
    classes?: Partial<
        Record<
            | 'primary'
            | 'secondary'
            | 'helperText'
            | 'errorText'
            | 'hasSecondary'
            | 'select',
            string
        >
    >;
};
// @deprecated Either use mui/select directly or refactor this to make props and types more defined. Prone to bugs
function SelectField<Value = unknown>(props: SelectFieldProps<Value>) {
    const {
        label,
        name,
        value,
        options,
        readOnly,
        onChange,
        FormHelperTextProps,
        helperText,
        errorText,
        fullWidth,
        rootRef,
        className,
        required,
        margin = 'dense',
        ...other
    } = props;
    const { classes, cx } = useStyles({
        classesOverrides: props.classes,
    });
    //console.log('value',value)
    // native={typeof window.orientation !== 'undefined' ? true : false }
    return (
        <FormControl
            fullWidth={fullWidth}
            ref={rootRef}
            className={className}
            error={!!errorText}
            required={required}
            margin={margin}
            {...other}
        >
            {<InputLabel htmlFor={name}>{label}</InputLabel>}
            <Select<Value | string>
                name={name}
                label={label}
                value={value || options.length >= 0 ? value : options[0].value}
                onChange={onChange}
                className={className}
                readOnly={readOnly}
                classes={{
                    root: classes.select,
                }}
            >
                {options.map((o) => (
                    <MenuItem
                        value={o.value}
                        key={o.value}
                        classes={{
                            root: cx({
                                [classes.hasSecondary]: !!o.description,
                            }),
                        }}
                    >
                        <ListItemText
                            primary={o.label}
                            secondary={o.description}
                            classes={{
                                primary: classes.primary,
                                secondary: classes.secondary,
                            }}
                        />
                    </MenuItem>
                ))}
            </Select>
            {helperText && (
                <FormHelperText
                    className={classes.helperText}
                    {...FormHelperTextProps}
                >
                    {helperText}
                </FormHelperText>
            )}
            {errorText && (
                <FormHelperText
                    className={classes.errorText}
                    {...FormHelperTextProps}
                >
                    {errorText}
                </FormHelperText>
            )}
        </FormControl>
    );
}

export default SelectField;
